import { Button, MCard, MSpinner } from "components/Shared";
import { KeyValueOriginal, MCardField } from "components/Shared/MCard";
import { PhoenixRuleDetailEdit, RuleRecommendationListItem } from "interfaces/Mueller.Business.Models";
import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "tools/routerCompat";
import type { RouteComponentProps } from "tools/routerCompat.d";
import { userHasRight } from "selectors/rights";
import { DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { FORM_DESIGN_EDIT, FORM_MAINTENANCE_EDIT } from "typeConstants/rights";
import { connect } from "react-redux";
import { saveRulesetDetail } from "requests/rulesRecommendations";
import { webApiDownloadFile } from "tools/fileDownloadTools";
import { SxProps } from "@mui/system";

interface Props extends RouteComponentProps {
  item: RuleRecommendationListItem;
  onSaveFinished: () => void;
  onCopyClick: () => void;
  infoCardSx: SxProps;
  actionsCardSx: SxProps;
  // Redux
  canEditRuleInfo: boolean;
  canEditFormDesign: boolean;
}

const RulesRecommendationsListDetails = ({
  item,
  history,
  canEditRuleInfo,
  canEditFormDesign,
  onSaveFinished,
  onCopyClick,
  infoCardSx = { height: "auto" },
  actionsCardSx = { height: "auto" },
}: Props) => {
  const mounted = useRef(true);
  const [isSaving, setIsSaving] = useState(false);

  const onEditClick = () => {
    history.push(`/phoenixforms-designer/rules-editor/${item.rowKey}`);
  };

  const onExportToCsvClick = () => {
    webApiDownloadFile(`/phoenixforms-rules-list/rules/${item.rowKey}/export-csv`);
  };

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  let cardFields: MCardField[];

  cardFields = [
    { editable: false, key: "name", label: "Name", value: item.name },
    { editable: true, key: "category", label: "Category", value: item.category },
    { editable: true, key: "description", label: "Description", value: item.description },
    { editable: false, key: "modifiedDate", label: "Last Update", value: item.modifiedDate, dataType: DATA_TYPE_DATE },
    { editable: false, key: "modifiedBy", label: "Updated By", value: item.modifiedBy },
    { editable: false, key: "publishDate", label: "Publish Date", value: item.publishDate, dataType: DATA_TYPE_DATE },
  ];

  const onSave = (changeArray: KeyValueOriginal<RuleRecommendationListItem>[]) => {
    let postData: Partial<PhoenixRuleDetailEdit> = {};

    // post data has the same fields as the data model, and also has a field to track if the value was changed
    changeArray.forEach(({ key, value }) => {
      postData[key] = value;
      postData[key + "Changed"] = true;
    });

    setIsSaving(true);
    saveRulesetDetail(
      item.rowKey,
      postData as PhoenixRuleDetailEdit,
      () => {
        onSaveFinished?.();
        setIsSaving(false);
      },
      null,
      mounted,
    );
  };

  return (
    <>
      <MCard
        title="Rule / Recommendation Information"
        fields={cardFields}
        isEditable={canEditRuleInfo}
        onEditFinished={onSave}
        isFetching={isSaving}
        sx={infoCardSx}
      />
      {canEditFormDesign && (
        <MCard
          title="Rule / Recommendation Actions"
          sx={actionsCardSx}
          fields={[
            {
              key: "buttons",
              control: (
                <div style={{ display: "flex", gap: 10 }}>
                  <Button primary raised onClick={onEditClick}>
                    Edit
                  </Button>
                  <Button primary raised onClick={onCopyClick}>
                    Copy
                  </Button>
                  <Button primary raised onClick={onExportToCsvClick}>
                    CSV
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  canEditRuleInfo: userHasRight(FORM_MAINTENANCE_EDIT)(state),
  canEditFormDesign: userHasRight(FORM_DESIGN_EDIT)(state),
});

export default withRouter(connect(mapStateToProps)(RulesRecommendationsListDetails));
